/**
 * Admin → Shops listing + create/edit modal.
 *
 * Owns the full page state (rows, zones, verticals, owners, form,
 * search) and the side-effects (load, save, approve, reject, toggle,
 * delete, backfill). Pure presentational chunks live in
 * ``./_partials/``:
 *   * ``ShopsTable``  — the listing table + action row
 *   * ``ShopForm``    — the create/edit form body
 *
 * NOTE: ``Field`` and ``Modal`` are re-exported from this module
 * because 8+ other admin/super/merchant pages import them. Don't
 * move them — would force a fan-out edit across the app.
 */
import { useEffect, useMemo, useState } from "react";
import { Plus, X, Loader2, Clock, Lock } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import AdminSearchBar from "../../components/AdminSearchBar";
import { isFoodShop } from "../../lib/foodCategory";
import ShopsTable from "./_partials/ShopsTable";
import ShopForm, { ZONE_AUTO } from "./_partials/ShopForm";
import { useTenant } from "../../contexts/TenantContext";

// Form scaffold — extended with ``lat`` / ``lng`` so the map picker
// has somewhere to write. Defaults to null so an unsaved-pin shop
// surfaces as "— unassigned" in the list (consistent with the
// backend's auto_assign_zone fallback).
const EMPTY = {
    name: "", description: "", food_type: "VEG_NON_VEG", cuisines: "",
    cover_image_url: "", logo_url: "", min_order_value: 99, status: "ACTIVE",
    cdc_enabled: false, contact_visible_to_customer: false,
    home_delivery_enabled: true, pickup_enabled: true, referral_code: "",
    business_category_ids: [], lat: null, lng: null,
    address_line1: "", address_city: "", address_pincode: "",
    // New shops default to auto-assign from the map pin — the behaviour
    // that predates the Delivery zone dropdown.
    zone_id: ZONE_AUTO,
    // Phase 2 / Feb 2026 — optional GST + contact + per-shop commission override.
    gst_number: "", gst_certificate_url: "",
    mobile_number: "", whatsapp_number: "", email: "",
    commission_override_pct: "", commission_override_amount: "",
    commission_override_kind: "PERCENT",
    delivery_fee_borne_by: null,
    free_delivery_enabled: false, free_delivery_above: 499,
    is_featured: false,
};

export default function AdminRestaurants() {
    const { tenant } = useTenant();
    const [rows, setRows] = useState([]);
    const [zones, setZones] = useState([]);
    const [verticals, setVerticals] = useState([]);
    const [owners, setOwners] = useState([]);
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [query, setQuery] = useState("");

    const load = () => Promise.all([
        api.get("/admin/restaurants").then(({ data }) => setRows(data)),
        api.get("/admin/zones").then(({ data }) => setZones(data)).catch(() => setZones([])),
        api.get("/admin/business-categories").then(({ data }) => setVerticals(data)).catch(() => setVerticals([])),
        api.get("/admin/owners").then(({ data }) => setOwners(data)).catch(() => setOwners([])),
    ]);
    useEffect(() => { load(); }, []);

    const ownersByShopId = useMemo(() => {
        const m = {};
        for (const o of owners) {
            for (const rid of o.restaurant_ids || []) {
                (m[rid] = m[rid] || []).push(o);
            }
        }
        return m;
    }, [owners]);

    const verticalById = useMemo(() => {
        const m = {};
        verticals.forEach((v) => { m[v.id || v.slug] = v; });
        return m;
    }, [verticals]);

    const parentVerticals = useMemo(
        () => (verticals || []).filter((v) => !v.parent_id && v.is_enabled !== false && v.status !== "INACTIVE"),
        [verticals],
    );
    const subVerticalsBy = useMemo(() => {
        const m = {};
        for (const v of verticals || []) {
            if (v.parent_id && v.status !== "INACTIVE") (m[v.parent_id] = m[v.parent_id] || []).push(v);
        }
        return m;
    }, [verticals]);

    const formShopForFoodCheck = useMemo(
        () => ({ business_category_ids: form.business_category_ids || [] }),
        [form.business_category_ids],
    );
    const showFoodType = isFoodShop(formShopForFoodCheck, verticals);

    const zoneById = useMemo(() => {
        const m = {};
        zones.forEach((z) => { m[z.id] = z; });
        return m;
    }, [zones]);

    const filtered = useMemo(() => {
        // iter62b — sort alphabetically by name (case-insensitive) so the
        // list is browseable. The /admin/restaurants API returns shops
        // in creation order which is useless once a tenant has 20+ shops.
        const sorted = [...rows].sort((a, b) =>
            (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }),
        );
        const q = query.trim().toLowerCase();
        if (!q) return sorted;
        return sorted.filter((r) => {
            const verticalNames = (r.business_category_ids || []).map((id) => (verticalById[id]?.title || verticalById[id]?.name || id)).join(" ");
            const ownerHay = (ownersByShopId[r.id] || []).map((o) => `${o.first_name || ""} ${o.last_name || ""} ${o.mobile_number || ""}`).join(" ");
            // Include shop phone + whatsapp in the search hay so admins
            // can find a shop by its contact number.
            const shopPhoneHay = `${r.mobile_number || ""} ${r.whatsapp_number || ""}`;
            const hay = `${r.name || ""} ${shopPhoneHay} ${(r.cuisines || []).join(" ")} ${verticalNames} ${ownerHay} ${r.food_type || ""} ${r.status || ""} ${zoneById[r.zone_id]?.name || ""}`.toLowerCase();
            return hay.includes(q);
        });
    }, [rows, query, zoneById, verticalById, ownersByShopId]);

    const openCreate = () => { setEdit(null); setForm(EMPTY); setOpen(true); };
    const openEdit = (r) => {
        const a = r.address || {};
        setEdit(r);
        setForm({
            name: r.name || "",
            description: r.description || "",
            food_type: r.food_type || "OTHER",
            cuisines: (r.cuisines || []).join(", "),
            cover_image_url: r.cover_image_url || "",
            logo_url: r.logo_url || "",
            min_order_value: r.min_order_value ?? 99,
            status: r.status || "ACTIVE",
            cdc_enabled: !!r.cdc_enabled,
            contact_visible_to_customer: !!r.contact_visible_to_customer,
            home_delivery_enabled: r.home_delivery_enabled !== false,
            pickup_enabled: r.pickup_enabled !== false,
            business_category_ids: r.business_category_ids || [],
            lat: a.lat ?? r.latitude ?? null,
            lng: a.lng ?? r.longitude ?? null,
            address_line1: a.line1 || "",
            address_city: a.city || "",
            address_pincode: a.pincode || "",
            // Show the shop's actual current zone as the selected value.
            // A shop with no zone shows "Auto" rather than "Unassigned" so
            // moving its map pin still re-derives a zone the way it always
            // did — the admin can still pick "Unassigned" explicitly.
            zone_id: r.zone_id || ZONE_AUTO,
            gst_number: r.gst_number || "",
            gst_certificate_url: r.gst_certificate_url || "",
            mobile_number: r.mobile_number || "",
            whatsapp_number: r.whatsapp_number || "",
            email: r.email || "",
            commission_override_pct: r.commission_override_pct ?? "",
            commission_override_amount: r.commission_override_amount ?? "",
            commission_override_kind: r.commission_override_amount != null && r.commission_override_pct == null ? "FLAT" : "PERCENT",
            delivery_fee_borne_by: r.delivery_fee_borne_by ?? null,
            free_delivery_enabled: !!r.free_delivery_enabled,
            free_delivery_above: r.free_delivery_above ?? 499,
            is_featured: !!r.is_featured,
        });
        setOpen(true);
    };

    const save = async () => {
        // Bug #9 guard — require at least one business category.
        if (!form.business_category_ids || form.business_category_ids.length === 0) {
            toast.error("Pick at least one business category for the shop.");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                name: form.name,
                description: form.description,
                food_type: showFoodType ? form.food_type : "OTHER",
                cuisines: form.cuisines.split(",").map((s) => s.trim()).filter(Boolean),
                cover_image_url: form.cover_image_url || null,
                logo_url: form.logo_url || null,
                min_order_value: Number(form.min_order_value),
                status: form.status,
                cdc_enabled: !!form.cdc_enabled,
                contact_visible_to_customer: !!form.contact_visible_to_customer,
                home_delivery_enabled: form.home_delivery_enabled !== false,
                pickup_enabled: form.pickup_enabled !== false,
                business_category_ids: form.business_category_ids || [],
                gst_number: form.gst_number || null,
                gst_certificate_url: form.gst_certificate_url || null,
                mobile_number: form.mobile_number || null,
                whatsapp_number: form.whatsapp_number || null,
                email: form.email || null,
                commission_override_pct: form.commission_override_kind === "PERCENT" && form.commission_override_pct !== "" ? Number(form.commission_override_pct) : null,
                commission_override_amount: form.commission_override_kind === "FLAT" && form.commission_override_amount !== "" ? Number(form.commission_override_amount) : null,
                delivery_fee_borne_by: form.delivery_fee_borne_by || null,
                free_delivery_enabled: !!form.free_delivery_enabled,
                free_delivery_above: form.free_delivery_above === "" || form.free_delivery_above == null ? null : Number(form.free_delivery_above),
                is_featured: !!form.is_featured,
            };
            if (form.lat != null && form.lng != null) {
                payload.address = {
                    line1: form.address_line1 || null,
                    city: form.address_city || null,
                    pincode: form.address_pincode || null,
                    lat: form.lat,
                    lng: form.lng,
                };
            }
            // Delivery zone. The KEY'S PRESENCE is the signal on both
            // endpoints: omit it entirely for "Auto" (backend derives the
            // zone from the map pin, exactly as it did before this
            // dropdown existed), send an id to pin it, or send null to
            // deliberately leave the shop unassigned.
            if (form.zone_id !== ZONE_AUTO) {
                payload.zone_id = form.zone_id || null;
            } else if (edit) {
                // "Auto" on an existing shop explicitly hands control back
                // to geo auto-assignment. Without this the backend can't
                // tell it apart from a PATCH that simply doesn't mention
                // the zone, so a previously pinned shop would stay pinned.
                payload.zone_pinned = false;
            }
            if (edit) {
                await api.patch(`/admin/restaurants/${edit.id}`, payload);
                toast.success("Shop updated");
            } else {
                // iter110 — admin path builds its payload inline (does
                // NOT go through shopFormToPayload), so we must add the
                // referral_code explicitly here. See test_report iter64
                // for the regression this snippet fixes.
                const code = (form.referral_code || "").trim().toUpperCase();
                if (code) payload.referral_code = code;
                const { data } = await api.post("/admin/restaurants", payload);
                if (code && !data?.referred_by_user_id) {
                    toast.warning("Saved, but referral code did not match any customer");
                } else if (code) {
                    toast.success("Shop added · linked to referrer");
                } else {
                    toast.success("Shop added");
                }
            }
            setOpen(false);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
    };

    const toggleStatus = async (r) => {
        const next = r.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
        await api.patch(`/admin/restaurants/${r.id}`, { status: next });
        toast.success(`${r.name} → ${next}`);
        load();
    };

    const approveShop = async (r) => {
        try {
            await api.post(`/admin/restaurants/${r.id}/approve`);
            toast.success(`${r.name} approved — now live`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to approve"); }
    };

    const rejectShop = async (r) => {
        const reason = window.prompt(`Reject "${r.name}"? Optional reason for the merchant:`, "");
        if (reason === null) return;
        try {
            await api.post(`/admin/restaurants/${r.id}/reject`, { reason });
            toast.success("Shop rejected");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to reject"); }
    };

    const remove = async (r) => {
        if (!confirm(`Delete "${r.name}"? Past orders will keep their reference for reporting.`)) return;
        try {
            const { data } = await api.delete(`/admin/restaurants/${r.id}`);
            toast.success(data?.mode === "archived"
                ? `Archived — ${data.orders} past order(s) kept for reporting`
                : "Deleted");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
    };

    const backfillHours = async () => {
        if (!confirm("Run health-check on every restaurant?\n\n• Fill missing weekdays with default 09:00–23:00 hours\n• Snap restaurants with no map coordinates to the first active zone\n• Auto-assign restaurants to their containing zone\n\nExisting data is preserved.")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/restaurants/backfill-hours");
            toast.success(`Scanned ${data.total} · Hours +${data.hours_updated} · Coords +${data.coords_updated} · Zone +${data.zone_assigned}`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Backfill failed"); } finally { setBusy(false); }
    };

    const red = rows.filter((r) => r.data_quality?.level === "red").length;
    const amber = rows.filter((r) => r.data_quality?.level === "amber").length;

    // iter104 — Single-vendor guard mirrors the merchant UI: once the
    // one allowed shop exists, hide "+ Add Shop" so admins don't try to
    // add a 2nd and hit a 409 from the backend. The backend still
    // enforces regardless.
    const isSingleVendor = (tenant?.vendor_mode || "MULTI_VENDOR") === "SINGLE_VENDOR";
    const singleVendorCapReached = isSingleVendor && rows.length > 0;

    return (
        <div>
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Shops</h1>
                    <p className="text-sm text-muted-foreground mt-1">{rows.length} total{query ? ` · ${filtered.length} match` : ""}</p>
                </div>
                <button
                    data-testid="add-restaurant"
                    onClick={openCreate}
                    disabled={singleVendorCapReached}
                    className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={singleVendorCapReached ? "Single-vendor mode — only 1 shop allowed" : ""}
                >
                    {singleVendorCapReached ? <Lock size={16} /> : <Plus size={16} />} Add Shop
                </button>
            </div>
            <div className="mt-4">
                <AdminSearchBar value={query} onChange={setQuery} placeholder="Search by name, vertical, keyword, zone…" testid="shops-search" count={filtered.length} />
            </div>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
                <button data-testid="backfill-hours-btn" disabled={busy} onClick={backfillHours} className="border border-border bg-muted/40 hover:bg-muted rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"><Clock size={13} /> Run health-check & backfill</button>
                {red + amber === 0
                    ? <span data-testid="health-summary" className="text-[11px] text-emerald-700 font-semibold inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"/> All restaurants healthy</span>
                    : (
                        <span data-testid="health-summary" className="text-[11px] text-muted-foreground inline-flex items-center gap-2">
                            {red > 0 && <span className="inline-flex items-center gap-1 text-red-600 font-semibold"><span className="w-2 h-2 rounded-full bg-red-500"/> {red} need attention</span>}
                            {amber > 0 && <span className="inline-flex items-center gap-1 text-amber-600 font-semibold"><span className="w-2 h-2 rounded-full bg-amber-500"/> {amber} minor</span>}
                        </span>
                    )
                }
            </div>

            <ShopsTable
                filtered={filtered}
                query={query}
                zoneById={zoneById}
                verticalById={verticalById}
                ownersByShopId={ownersByShopId}
                onEdit={openEdit}
                onDelete={remove}
                onToggleStatus={toggleStatus}
                onApprove={approveShop}
                onReject={rejectShop}
            />

            {open && (
                <Modal onClose={() => setOpen(false)} title={edit ? `Edit · ${edit.name}` : "Add Shop"}>
                    <ShopForm
                        form={form}
                        setForm={setForm}
                        edit={edit}
                        showFoodType={showFoodType}
                        parentVerticals={parentVerticals}
                        subVerticalsBy={subVerticalsBy}
                        zones={zones}
                    />
                    <button data-testid="save-restaurant" disabled={busy || !form.name} onClick={save} className="mt-4 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                        {busy && <Loader2 size={16} className="animate-spin" />} {edit ? "Save changes" : "Save"}
                    </button>
                </Modal>
            )}
        </div>
    );
}

// Re-exported (used by 8+ other admin/super/merchant pages — do not move).
export function Field({ label, children, full }) {
    return (
        <label className={`flex flex-col gap-1 ${full ? "col-span-2" : ""}`}>
            <span className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">{label}</span>
            {children}
        </label>
    );
}

export function Modal({ title, onClose, children }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-card rounded-2xl p-5 w-full max-w-lg max-h-[85vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-lg">{title}</h3>
                    <button onClick={onClose}><X size={20} /></button>
                </div>
                {children}
            </div>
            <style>{`.inp{background:hsl(var(--muted));border-radius:0.75rem;padding:0.6rem 0.75rem;outline:none;font-size:0.875rem;width:100%}`}</style>
        </div>
    );
}
